import * as React from 'react';

import styled from '@emotion/styled';

import { Spacer } from '@shared/components/helpers';

import { Distance } from '@admin/components/helpers/distance';
import { Titleize } from '@admin/components/helpers/titleize';
import { Timestamp } from '@admin/components/timestamp';
import { palletURL } from '@admin/config/routes';
import {
  Changed__Association__Kind,
  Changed__Event,
  HistoryResourceChangedAuditedFragment,
  HistoryResourceChangedAuditFragment,
  HistoryResourceChangedFieldFragment,
  HistoryResourceChangedRelationshipFragment,
} from '@admin/schema';

import { Autoformat } from './audit/autoformat';
import { CUFT } from './audit/cuft';
import { Duration, Durations } from './audit/duration';
import { Phone } from './audit/phone';
import { Tags } from './audit/tags';

const Link = styled.a`
  color: #3891d1;
  text-decoration: underline;
`;

const PRIMARY_EMAIL_FIELD_NAME = 'primary_email';
const PRIMARY_PHONE_FIELD_NAME = 'primary';

// Fields to skip when a composite parent field exists
// Key: parent field name, Value: array of child fields to hide
const COMPOSITE_FIELD_CHILDREN: Record<string, string[]> = {
  sku_id: ['sku_value', 'sku_description'],
};

const filteredFields = (resource: HistoryResourceChangedAuditFragment): HistoryResourceChangedFieldFragment[] => {
  return resource.fields.filter((field) => {
    // Check if this field should be hidden because its parent composite field exists
    for (const [parentField, childFields] of Object.entries(COMPOSITE_FIELD_CHILDREN)) {
      if (childFields.includes(field.name) && resource.fields.some((f) => f.name === parentField)) {
        return false;
      }
    }
    return true;
  });
};

const Change: React.FC<{
  name: string;
  value: string | number | boolean | string[] | number[] | boolean[] | null;
  timezone?: string;
}> = ({ name, value, timezone }) => {
  switch (name) {
    case 'tags':
      return <Tags value={value as string[]} />;
    case 'phone':
      return <Phone value={value as string} />;
    case 'break':
      return <Duration value={value as number} />;
    case 'estimated_duration':
      return <Duration value={value as number} />;
    case 'estimated_distance':
      return <Distance value={value as number} />;
    case 'estimated_cuft':
      return <CUFT value={value as number} />;
    case 'estimated_mover_durations':
      return <Durations values={value as number[]} />;
    default:
      return <Autoformat value={value} timezone={timezone} />;
  }
};

const Field: React.FC<{
  audit: HistoryResourceChangedAuditFragment;
  field: HistoryResourceChangedFieldFragment;
  timezone?: string;
}> = ({ audit, field, timezone }) => {
  // Handle SKU ID changes with resolved values - combines sku_id, sku_value, and sku_description
  if (field.name === 'sku_id') {
    const skuValueField = audit.fields.find(({ name }) => name === 'sku_value');
    const skuDescField = audit.fields.find(({ name }) => name === 'sku_description');

    const oldSkuValue = skuValueField?.was || `SKU #${field.was}`;
    const oldSkuDesc = skuDescField?.was;
    const newSkuValue = skuValueField?.now || `SKU #${field.now}`;
    const newSkuDesc = skuDescField?.now;

    return (
      <>
        <strong>SKU</strong> was previously <strong>{oldSkuValue}</strong>
        {oldSkuDesc && <> ({oldSkuDesc})</>}
        {' now '}
        <strong>{newSkuValue}</strong>
        {newSkuDesc && <> ({newSkuDesc})</>}
      </>
    );
  }

  // Skip rendering sku_value and sku_description separately when they're part of a SKU change
  if (
    (field.name === 'sku_value' || field.name === 'sku_description') &&
    audit.fields.some((f) => f.name === 'sku_id')
  ) {
    return null;
  }

  // Handle quantity changes for PalletInventory - show the SKU alongside quantity
  if (
    field.name === 'quantity' &&
    audit.audited.__typename === 'Changed__Record' &&
    audit.audited.type === 'Logistics::PalletInventory'
  ) {
    const skuValueField = audit.fields.find(({ name }) => name === 'sku_value');
    const skuValue = skuValueField?.now || skuValueField?.was;

    return (
      <>
        <strong>Quantity</strong> is now <strong>{field.now}</strong>
        {skuValue && (
          <>
            {' '}
            for SKU: <strong>{skuValue}</strong>
          </>
        )}
      </>
    );
  }

  if (audit.audited.__typename === 'Changed__Phone' && field.name === PRIMARY_PHONE_FIELD_NAME) {
    const changed = audit.fields.find(({ name }) => name === 'number');
    if (field.was) {
      const phone = (changed && changed.was) || audit.audited.number;
      return (
        <>
          The <strong>primary phone</strong> for this account was previously <strong>{phone}</strong>
        </>
      );
    }
    if (field.now) {
      const phone = (changed && changed.now) || audit.audited.number;
      return (
        <>
          The <strong>primary phone</strong> for this account is now <strong>{phone}</strong>
        </>
      );
    }
  }

  if (audit.audited.__typename === 'Changed__Customer' && field.name === PRIMARY_EMAIL_FIELD_NAME) {
    const changed = audit.fields.find(({ name }) => name === 'email');

    if (field.was) {
      const email = (changed && changed.was) || audit.audited.email;
      return (
        <>
          The <strong>primary email</strong> for this account was previously <strong>{email}</strong>
        </>
      );
    }
    if (field.now) {
      const email = (changed && changed.now) || audit.audited.email;
      return (
        <>
          The <strong>primary email</strong> for this account is now <strong>{email}</strong>
        </>
      );
    }
  }

  if (audit.audited.__typename === 'Changed__Record' && audit.audited.type === 'User') {
    if (field.name === 'password_changed_at') {
      return (
        <>
          The user&apos;s <strong>password</strong> was changed.
          {field.now != null ? (
            <>
              {' '}
              <Timestamp value={String(field.now)} timezone={timezone} />.
            </>
          ) : null}
        </>
      );
    }
    if (field.name === 'reset_password_sent_at') {
      if (field.now != null && field.was == null) {
        return (
          <>
            A <strong>password reset</strong> was requested <Timestamp value={String(field.now)} timezone={timezone} />.
          </>
        );
      }
      return (
        <>
          <strong>Password reset timestamp</strong> was previously{' '}
          <Change value={field.was} name={field.name} timezone={timezone} />
          {field.now != null ? (
            <>
              {' '}
              <span>now</span> <Change value={field.now} name={field.name} timezone={timezone} />
            </>
          ) : (
            <>
              {' '}
              <span>now</span> cleared
            </>
          )}
        </>
      );
    }
    if (field.name === 'failed_attempts') {
      return (
        <>
          <strong>Failed sign-in attempts</strong> were previously <strong>{field.was ?? 'none'}</strong>
          {' now '}
          <strong>{field.now ?? 'none'}</strong>.
        </>
      );
    }
    if (field.name === 'locked_at') {
      if (field.now != null && (field.was == null || field.was === undefined)) {
        return (
          <>
            The account was <strong>locked</strong> <Timestamp value={String(field.now)} timezone={timezone} />.
          </>
        );
      }
      if ((field.now == null || field.now === undefined) && field.was != null) {
        return (
          <>
            The account was <strong>unlocked</strong> (lock ended or was cleared).
          </>
        );
      }
    }
  }

  return (
    <>
      <strong>
        {field.name === 'tags' && 'Account'} <Titleize text={field.name} />
      </strong>
      {field.was !== null && (
        <>
          {' '}
          <span>was</span>{' '}
          <strong>
            <Change value={field.was} name={field.name} timezone={timezone} />
          </strong>
        </>
      )}
      {field.now !== null && (
        <>
          {' '}
          <span>now</span>{' '}
          <strong>
            <Change value={field.now} name={field.name} timezone={timezone} />
          </strong>
        </>
      )}
    </>
  );
};

const AssociatedLink: React.FC<{
  relationshipName: string;
  associated: {
    id: string;
    name: string;
  };
}> = ({ relationshipName, associated }) => {
  switch (relationshipName) {
    case 'pallets':
      return <Link href={palletURL(associated.id)}>{associated.name}</Link>;
    default:
      return <strong>{associated.name}</strong>;
  }
};

const Relationship: React.FC<{
  relationship: HistoryResourceChangedRelationshipFragment;
}> = ({ relationship }) => (
  <>
    <strong>
      <Titleize text={relationship.name} />
    </strong>{' '}
    <span>changed</span>{' '}
    {relationship.associations.map((association, subindex) => (
      <span key={subindex}>
        {' '}
        <span>
          <span>
            {association.kind === Changed__Association__Kind.Add && <span className="text-success">(added)</span>}
            {association.kind === Changed__Association__Kind.Remove && <span className="text-danger">(removed)</span>}
          </span>{' '}
          <AssociatedLink relationshipName={relationship.name} associated={association.associated} />
        </span>
      </span>
    ))}
  </>
);

const AuditedType: React.FC<{
  audited: HistoryResourceChangedAuditedFragment;
}> = ({ audited }) => (
  <strong>
    {audited.__typename === 'LaborClock' && `Labor Clock: ${audited.user.name} (${audited.role.toLowerCase()})`}
    <Spacer height="1rem" />
  </strong>
);

export const Audit: React.FC<{
  resource: HistoryResourceChangedAuditFragment;
  timestamp: string;
  timezone?: string;
}> = ({ resource, timestamp, timezone }) => {
  // Special handling for TrainingQualification audits
  if (resource.audited.__typename === 'Changed__Record' && resource.audited.type === 'TrainingQualification') {
    const roleField = resource.fields.find(({ name }) => name === 'role');
    const deletedAtField = resource.fields.find(({ name }) => name === 'deleted_at');

    let message = '';

    // Handle creation of training qualification
    if (resource.event === Changed__Event.Create && roleField && roleField.now) {
      message = `Training qualification for ${roleField.now} role was added`;
    }
    // Handle soft deletion (deactivation) - treat reactivation as "added" too
    else if (resource.event === Changed__Event.Update && deletedAtField) {
      const role = roleField?.now || roleField?.was;
      if (deletedAtField.was === null && deletedAtField.now !== null) {
        // Being soft deleted
        message = `Training qualification for ${role} role was removed`;
      } else if (deletedAtField.was !== null && deletedAtField.now === null) {
        // Being reactivated - treat as "added"
        message = `Training qualification for ${role} role was added`;
      }
    }

    if (message) {
      return (
        <tr>
          <td className="col-nowrap col-md-2 text-left" colSpan={2}>
            <Timestamp value={timestamp} timezone={timezone} />
          </td>
          <td className="col-nowrap col-md-2 text-left" colSpan={2}>
            <strong>{resource.changer.name}</strong>
          </td>
          <td className="col-md-8 text-left" colSpan={8}>
            <strong>{message}</strong>
          </td>
        </tr>
      );
    }
  }

  // Default audit rendering
  return (
    <tr>
      <td className="col-nowrap col-md-2 text-left" colSpan={2}>
        <Timestamp value={timestamp} timezone={timezone} />
      </td>
      <td className="col-nowrap col-md-2 text-left" colSpan={2}>
        <strong>{resource.changer.name}</strong>{' '}
        <span>
          {resource.event === Changed__Event.Create && <>created</>}
          {resource.event === Changed__Event.Update && <>updated</>}
          {resource.event === Changed__Event.Destroy && <>destroyed</>}
        </span>
      </td>
      <td className="col-md-8 text-left" colSpan={8}>
        {(resource.audited.__typename === 'Changed__Record' || resource.audited.__typename === 'LaborClock') && (
          <AuditedType audited={resource.audited} />
        )}
        <ul>
          {filteredFields(resource).map((field, index) => (
            <li key={index}>
              <Field audit={resource} field={field} timezone={timezone} />
            </li>
          ))}
          {resource.relationships.map((relationship, index) => (
            <li key={index}>
              <Relationship relationship={relationship} />
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
};
